import { useEffect, useRef } from 'react';
import {
    PrequalsApi,
    LoanApplication,
    SubmitLoanApplicationResponseDto,
    PropertyType,
    LoanApplicationSubmitSource,
} from '@servicetitan/financing-api-client/src/prequals-api';
import {
    ApplicationDataStore,
    SubmittingUserStore,
    preparePrequalificationData,
} from '@servicetitan/financing-application-shared';
import { FinancedAmountStore, SelectedPlanStore } from '@servicetitan/financing-plans-shared';
import { useDependencies } from '@servicetitan/react-ioc';
import { useMutation } from '@tanstack/react-query';
import { SetupDataStore, useProviderConfig } from '@servicetitan/financing-workflow-shared';
import { ServerError } from '@servicetitan/financing-utils-shared';

// Module-level fingerprint persistence to survive hook re-initializations
let persistedFingerprint: string | undefined;

export interface UsePrequalificationSubmissionOptions {
    /** The prequalification ID to submit */
    prequalId: string;
    /** Whether to automatically submit on mount (default: true) */
    autoSubmit?: boolean;
    /** GreenSky fingerprint (if applicable) */
    fingerprint?: string;
    /** Custom success callback - if provided, overrides default workflow stage transition */
    onSuccess?: (data: { data: SubmitLoanApplicationResponseDto }) => void;
    /** Custom error callback */
    onError?: (error: ServerError) => void;
}

export interface UsePrequalificationSubmissionReturn {
    /** Submit the prequalification application manually */
    submitPrequalification: () => void;
    /** Whether the prequalification is currently being submitted */
    isSubmitting: boolean;
    /** Whether the prequalification has been submitted successfully */
    isSuccess: boolean;
    /** Any error that occurred during submission */
    error: ServerError | null;
    /** Whether this is a GreenSky provider */
    isGreenSky: boolean;
    /** GreenSky fingerprint (if applicable) */
    fingerprint: string | undefined;
    /** Reset the submission state */
    reset: () => void;
    /** Loan ID from submission response */
    loanId: string | null;
}

export const usePrequalificationHardSubmission = (
    options: UsePrequalificationSubmissionOptions,
): UsePrequalificationSubmissionReturn => {
    const {
        prequalId,
        autoSubmit = true,
        fingerprint: passedFingerprint,
        onSuccess,
        onError,
    } = options;

    const [prequalsApi] = useDependencies(PrequalsApi);
    const { applicationData } = ApplicationDataStore.useStore();
    const { selectedPlan } = SelectedPlanStore.useStore();
    const { financedAmount, saleAmount, downPayment } = FinancedAmountStore.useStore();
    const { setupData } = SetupDataStore.useStore();
    const { submittingUser } = SubmittingUserStore.useStore();
    const { hasMailingAddress } = useProviderConfig();

    // Track submission state to prevent double submission in strict mode
    const hasSubmittedRef = useRef(false);

    // Use GreenSky Iovation
    const isGreenSky = setupData?.provider?.toLowerCase() === 'greensky';

    // Use persisted fingerprint if available, otherwise use passed fingerprint
    const fingerprint = persistedFingerprint ?? passedFingerprint;

    // Update persisted fingerprint when a new one is captured
    if (isGreenSky && passedFingerprint && !persistedFingerprint) {
        persistedFingerprint = passedFingerprint;
    }

    const prepareApplication = (): LoanApplication => {
        return preparePrequalificationData({
            applicationData,
            prequalId,
            tenantId: setupData!.tenantId,
            buId: setupData!.buId,
            jobId: setupData?.jobId ?? null,
            customerId: setupData!.customerId,
            salesAmount: saleAmount,
            downPayment,
            appliedOn: new Date().toISOString(),
            propertyType: PropertyType.SingleFamily,
            planNumber: selectedPlan?.planNumber ?? '',
            requestedAmount: financedAmount,
            technicianId: setupData?.technicianId ?? undefined,
            technicianLastName: submittingUser?.lastName ?? undefined,
            technicianPhone: submittingUser?.phone ?? undefined,
            technicianEmail: submittingUser?.email ?? undefined,
            source: applicationData.source as unknown as LoanApplicationSubmitSource,
            hasMailingAddress,
            referrer: setupData?.referrer,
        });
    };

    const mutation = useMutation<
        { data: SubmitLoanApplicationResponseDto },
        ServerError,
        LoanApplication
    >({
        mutationKey: ['submitPrequal', prequalId],
        mutationFn: (data: LoanApplication) =>
            prequalsApi.prequalificationsSubmitLoanApplication(data),
        onSuccess: (data: { data: SubmitLoanApplicationResponseDto }) => {
            hasSubmittedRef.current = true;

            if (onSuccess) {
                onSuccess(data);
            }
        },
        onError: (error: ServerError) => {
            // Reset submission state on error to allow retry
            hasSubmittedRef.current = false;

            if (onError) {
                onError(error);
            }
        },
    });

    const submitPrequalification = () => {
        if (!hasSubmittedRef.current) {
            // For GreenSky, wait for fingerprint before submitting
            if (isGreenSky && !fingerprint) {
                return;
            }
            mutation.mutate(prepareApplication());
        }
    };

    const reset = () => {
        hasSubmittedRef.current = false;
        mutation.reset();
        // Reset persisted fingerprint for GreenSky to allow fresh capture on retry
        if (isGreenSky) {
            persistedFingerprint = undefined;
        }
    };

    // Auto-submit effect
    useEffect(() => {
        if (autoSubmit && !hasSubmittedRef.current) {
            // For GreenSky, wait for fingerprint before submitting
            if (isGreenSky && !fingerprint) {
                return;
            }
            submitPrequalification();
        }

        return () => {
            // Reset submission state when component unmounts
            hasSubmittedRef.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoSubmit, isGreenSky, fingerprint]);

    return {
        submitPrequalification,
        isSubmitting: mutation.isPending,
        isSuccess: mutation.isSuccess,
        error: mutation.error as ServerError | null,
        isGreenSky,
        fingerprint,
        loanId: mutation.data?.data.loanId ?? null,
        reset,
    };
};
